/**
 * Speaker separation is English-only for now.
 *
 * `speakerSeparationEngineId` is a single engine id, but Speechmatics — which
 * is what it points at — ships one engine per language, the same way
 * transcription does. Transcription already models that as the per-language
 * `transcriptionEngines` map; separation does not, so a French TDO would be
 * sent to the English separation model and come back with speaker boundaries
 * drawn from the wrong acoustics.
 *
 * Rather than ship that silently, the capability is offered only for English
 * until the config grows a per-language map of its own. That is deliberately a
 * follow-on: making `speakerSeparationEngineId` a map is a config shape change
 * and is worth doing before orgs put the scalar in their application config
 * and it becomes a migration.
 */
export const SPEAKER_SEPARATION_LANGUAGE_CODE = 'en';

/** Whether separation can be offered for a transcription language code. */
export const supportsSpeakerSeparation = (languageCode?: string): boolean =>
  languageCode === SPEAKER_SEPARATION_LANGUAGE_CODE;

/**
 * The language a transcription engine id is configured under, for deciding
 * about a transcript that already exists — the language it was run in is not
 * recorded on the transcript, only the engine that produced it.
 *
 * Undefined when the id is not in the map at all, which is the pre-map
 * single-engine setup; callers treat that as English, since that is what the
 * single configured engine has always been.
 */
export const languageOfTranscriptionEngine = (
  transcriptionEngines: Record<string, string> | null | undefined,
  engineId: string | undefined
): string | undefined => {
  if (!engineId || !transcriptionEngines) {
    return undefined;
  }
  return Object.keys(transcriptionEngines).find(
    (code) => transcriptionEngines[code] === engineId
  );
};
